/**
 * Created: 2026-03-18
 * Initial commit: Add order detail dialog for admin order view
 *
 * Dialog component that shows detailed order information when an order row
 * is clicked in the OrdersPage. Adapted from tc-mini's order page for the
 * admin frontend, without customer-facing mutations.
 */

import { gql } from '@apollo/client'
import { useMutation, useQuery } from '@apollo/client/react'
import { dateFormat, message } from '@homemaster/jslib'
import CloseIcon from '@mui/icons-material/Close'
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import {
  GetOrderDetailDocument,
  OrderStatus,
  RefundOrderDocument,
} from '../gql/graphql'
import { getWeekdayText, mergeConsecutiveSlots } from '../lib/booking'

// Created: 2026-03-18 | Initial commit: Add order detail query for admin dialog
gql`
  query GetOrderDetail($id: UUID!) {
    order(id: $id) {
      id
      siteId
      status
      amount
      detail {
        courtId
        courtName
        price
        startTime
      }
      user {
        id
        name
        mobile
        avatar
        openid
      }
      payments {
        id
        amount
        createTime
        payTime
        creatorName
      }
      createTime
      payTime
      cancelTime
      expiresAt
    }
  }
`

// Created: 2026-03-18 | Initial commit: Add refund_order mutation for admin
gql`
  mutation RefundOrder($orderId: UUID!, $amount: Decimal!) {
    refundOrder(orderId: $orderId, amount: $amount) {
      ... on GraphqlSuccess {
        __typename
      }
      ... on GraphqlRequestError {
        __typename
        code
        message
      }
    }
  }
`

const statusLabels: Record<
  OrderStatus,
  { label: string; color: 'success' | 'warning' | 'default' | 'error' }
> = {
  PENDING: { label: '待支付', color: 'warning' },
  CONFIRMED: { label: '已支付', color: 'success' },
  CANCELLED: { label: '已取消', color: 'default' },
  CLOSED: { label: '已关闭', color: 'error' },
}

interface OrderDialogProps {
  orderId: string | null
  onClose: () => void
}

export default function OrderDialog({ orderId, onClose }: OrderDialogProps) {
  const { data, loading, refetch } = useQuery(GetOrderDetailDocument, {
    variables: { id: orderId ?? '' },
    skip: !orderId,
    fetchPolicy: 'network-only',
  })

  const [refundMutation, { loading: refunding }] = useMutation(RefundOrderDocument)

  const [refundOpen, setRefundOpen] = useState(false)
  const [refundAmount, setRefundAmount] = useState('')
  const [refundError, setRefundError] = useState('')

  const order = data?.order

  const mergedSlots = mergeConsecutiveSlots(
    order?.detail?.map((slot) => ({
      courtId: slot.courtId,
      courtName: slot.courtName,
      startTime: slot.startTime,
      price: Number(slot.price),
    })) ?? [],
  )

  const netPaid = order
    ? order.payments.reduce((sum, p) => sum + Number(p.amount), 0)
    : 0
  const maxRefund = Math.round(netPaid * 100) / 100

  const refundValue = Number(refundAmount)
  const refundValid =
    refundValue >= 0.01 && refundValue <= maxRefund && !Number.isNaN(refundValue)

  return (
    <Dialog open={!!orderId} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
        }}
      >
        订单详情
        <Box sx={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>
          {order && (
            <Chip
              label={statusLabels[order.status].label}
              color={statusLabels[order.status].color}
              size="small"
            />
          )}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex' }}>
          <IconButton onClick={() => refetch()} size="small" disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'grey.100', p: 1.5 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {order && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Slot details */}
            {mergedSlots.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    预订详情
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>场地 / 时段</TableCell>
                        <TableCell align="right">单价</TableCell>
                        <TableCell align="right">小时</TableCell>
                        <TableCell align="right">小计</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedSlots.map((slot) => {
                        const weekday = getWeekdayText(slot.date)
                        const startHour = slot.startHour.toString().padStart(2, '0')
                        const endHour = slot.endHour.toString().padStart(2, '0')
                        const hours = slot.endHour - slot.startHour
                        const unitPrice = slot.price / hours

                        return (
                          <TableRow
                            key={`${slot.courtId}-${slot.date}-${slot.startHour}`}
                          >
                            <TableCell>
                              <div>{slot.courtName}</div>
                              <Typography variant="caption" color="text.secondary">
                                {slot.date} {weekday} {startHour}:00-{endHour}:00
                              </Typography>
                            </TableCell>
                            <TableCell align="right">¥{unitPrice}</TableCell>
                            <TableCell align="right">x{hours}</TableCell>
                            <TableCell align="right">¥{slot.price}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 1,
                      mt: 0,
                      pt: 1,
                      borderTop: '1px solid',
                      borderColor: 'text.primary',
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {order.status === OrderStatus.Pending
                        ? '待支付金额:'
                        : '订单总额:'}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      color="error.main"
                      fontWeight="bold"
                    >
                      ¥{Number(order.amount).toFixed(2)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Order info */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  订单信息
                </Typography>
                <InfoRow label="订单号" value={order.id} />
                <InfoRow
                  label="客户"
                  value={[order.user.name, order.user.mobile].filter(Boolean).join(' ')}
                />
                <InfoRow
                  label="下单时间"
                  value={dateFormat(order.createTime, {
                    format: 'yyyy-MM-dd HH:mm:ss',
                  })}
                />
                {order.payTime && (
                  <InfoRow
                    label="支付时间"
                    value={dateFormat(order.payTime, { format: 'yyyy-MM-dd HH:mm:ss' })}
                  />
                )}
                {order.cancelTime && (
                  <InfoRow
                    label="取消时间"
                    value={dateFormat(order.cancelTime, {
                      format: 'yyyy-MM-dd HH:mm:ss',
                    })}
                  />
                )}
              </CardContent>
            </Card>

            {/* Payment records */}
            {order.payments.length > 0 && (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                    支付记录
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>时间</TableCell>
                        <TableCell>操作人</TableCell>
                        <TableCell align="right">金额</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {order.payments.map((payment) => {
                        const amount = Number(payment.amount)
                        const isRefund = amount < 0
                        return (
                          <TableRow key={payment.id}>
                            <TableCell>
                              {payment.payTime
                                ? dateFormat(payment.payTime, {
                                    format: 'yyyy-MM-dd HH:mm:ss',
                                  })
                                : isRefund
                                  ? '退款中'
                                  : dateFormat(payment.createTime, {
                                      format: 'yyyy-MM-dd HH:mm:ss',
                                    })}
                            </TableCell>
                            <TableCell>{payment.creatorName ?? '-'}</TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                color: isRefund ? 'error.main' : 'success.main',
                                fontWeight: 'bold',
                              }}
                            >
                              {isRefund ? '-' : '+'}¥{Math.abs(amount).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                  {maxRefund > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={() => {
                          setRefundAmount('')
                          setRefundOpen(true)
                        }}
                      >
                        退款
                      </Button>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Refund dialog */}
            <Dialog
              open={refundOpen}
              onClose={() => setRefundOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>发起退款</DialogTitle>
              <DialogContent>
                <TextField
                  autoFocus
                  fullWidth
                  label="退款金额"
                  type="text"
                  inputMode="decimal"
                  value={refundAmount}
                  onChange={(e) => {
                    const raw = e.target.value
                    // only allow digits and a single decimal point
                    if (raw === '' || /^\d*\.?\d{0,2}$/.test(raw)) {
                      setRefundAmount(raw)
                    }
                  }}
                  onBlur={() => {
                    const v = Number(refundAmount)
                    if (!refundAmount || Number.isNaN(v)) return
                    const clamped = Math.min(Math.max(v, 0.01), maxRefund)
                    setRefundAmount(String(Math.round(clamped * 100) / 100))
                  }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">¥</InputAdornment>
                      ),
                    },
                  }}
                  helperText={refundError || `最多可退 ¥${maxRefund.toFixed(2)}`}
                  error={!!refundError}
                  sx={{ mt: 1 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRefundOpen(false)} disabled={refunding}>
                  取消
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  disabled={!refundValid || refunding}
                  loading={refunding}
                  onClick={async () => {
                    setRefundError('')
                    const result = await refundMutation({
                      variables: { orderId: order?.id ?? '', amount: refundAmount },
                    })
                    const response = result.data?.refundOrder
                    if (response?.__typename === 'GraphqlRequestError') {
                      setRefundError(response.message ?? '退款失败')
                    } else {
                      setRefundOpen(false)
                      setRefundAmount('')
                      message.success('退款申请已提交', { autoHide: 3000 })
                      setTimeout(refetch, 2000)
                    }
                  }}
                >
                  确认退款
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ userSelect: 'text' }}>
        {value}
      </Typography>
    </Box>
  )
}
